import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface KeywordsHeaderProps {
  keywordName: string;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  onSave?: () => void;
  onExport?: () => void;
  keywords?: any[]; // Add keywords prop for export
}

const KeywordsHeader: React.FC<KeywordsHeaderProps> = ({
  keywordName,
  searchTerm,
  setSearchTerm,
  onSave,
  onExport,
  keywords = [],
}) => {
  const navigate = useNavigate();

  const exportToCSV = () => {
    if (!keywords || keywords.length === 0) {
      alert('No keywords to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Keyword',
      'Volume',
      'Difficulty Score',
      'Competition',
      'Low CPC',
      'High CPC',
      'Category',
      'Created At'
    ];

    // Convert keywords to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...keywords.map(keyword => [
        `"${keyword.keyword_text || ''}"`,
        keyword.volume || '',
        keyword.da_score || '',
        `"${keyword.competition || ''}"`,
        keyword.low_cpc || '',
        keyword.high_cpc || '',
        `"${keyword.category_display || ''}"`,
        keyword.created_at || ''
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${keywordName}_keywords_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className="w-full">
      <CardContent className="p-0">
        <div className="flex items-center justify-between">
          {/* Left side - Back button and keyword name */}
          <div className="flex flex-1 items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/research')}
              className="p-1.5"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex flex-row">
              <span className="text-muted-foreground mr-2">Research for:</span>
              <h1 className="text-base font-semibold text-foreground">{keywordName}</h1>
            </div>
          </div>

          {/* Center - Search bar */}
          <div className="flex-1 max-w-lg mr-4">
            <Input
              placeholder="Search keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-9"
            />
          </div>

          {/* Right side - Save and Export buttons */}
          <div className="flex items-center space-x-2">
            {/* <Button
              variant="outline"
              size="sm"
              onClick={onSave}
              className="flex items-center space-x-1.5 h-8 px-3"
            >
              <Save className="h-3.5 w-3.5" />
              <span className="text-sm">Save</span>
            </Button> */}
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              className="flex items-center space-x-1.5 h-8 px-3"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="text-sm">Export</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default KeywordsHeader;
